import { CalendarDays, Target, RotateCcw, Lock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eyebrow, SectionHeader, FadeIn } from "./primitives";
import { counters } from "@/data/dashboard";
import { toast } from "@/components/ui/sonner";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { cn } from "@/lib/utils";
import { DASHBOARD } from "@/constants/testIds";

const today = new Date().toLocaleDateString(undefined, {
  weekday: "long",
  day: "numeric",
  month: "long",
});

const Tag = ({ code, note }) => (
  <span className="flex items-center gap-2">
    <span className="inline-flex min-w-[2.5rem] justify-center rounded-md bg-secondary px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-foreground/80">
      {code}
    </span>
    <span className="text-[13px] text-muted-foreground">{note}</span>
  </span>
);

const CounterBlock = ({ data, className }) => (
  <div className={className}>
    <Eyebrow>{data.heading}</Eyebrow>
    <div className="mt-3 space-y-1.5">
      {data.tags.map((t) => (
        <Tag key={t.code} code={t.code} note={t.note} />
      ))}
    </div>
    <div className="mt-4 flex items-baseline gap-1.5">
      <span className="font-display text-5xl font-medium tabular-nums tracking-tight text-foreground">
        {data.days.toLocaleString()}
      </span>
      <span className="text-sm text-muted-foreground">days</span>
    </div>
    <p className="mt-2 flex items-center gap-1.5 text-[13px] text-muted-foreground">
      <CalendarDays className="h-3.5 w-3.5" /> since {data.since}
    </p>
    <Progress value={data.progress} className="mt-4 h-1 bg-secondary" />
    <div className="mt-4 space-y-2 text-[13px] text-muted-foreground">
      <div className="flex items-start gap-2">
        <Target className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary/80" />
        <span>
          Next milestone: <span className="text-foreground/80">{data.nextMilestone}</span>
          <span className="block text-muted-foreground/70">{data.daysToGo} days to go</span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <RotateCcw className="h-3.5 w-3.5 shrink-0" />
        <span>Last milestone: {data.lastMilestone}</span>
      </div>
    </div>
  </div>
);

const MoodPill = ({ active, onClick, tone, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "rounded-full border px-3 py-1 text-[13px] transition-colors",
      active
        ? tone === "success"
          ? "border-success/40 bg-success/15 text-success"
          : "border-warning/40 bg-warning/15 text-warning"
        : "border-border text-muted-foreground hover:border-border-strong hover:text-foreground",
    )}
  >
    {children}
  </button>
);

const IntentionCard = () => {
  const [line, setLine] = useLocalStorage("ts.intention.line", "");
  const [mood, setMood] = useLocalStorage("ts.intention.mood", "");

  const save = () =>
    toast.success("Saved for today", {
      description: line.trim() ? "One line is enough." : "Even a blank page counts.",
    });

  return (
    <Card className="flex h-full flex-col p-5 shadow-soft">
      <div className="flex items-start justify-between gap-3">
        <Eyebrow>Today’s Intention</Eyebrow>
        <span className="flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
          <Lock className="h-3 w-3" /> Private to you
        </span>
      </div>
      <p className="mt-3 font-display text-lg italic leading-snug text-foreground/90">
        “One line. What’s the smallest true thing today?”
      </p>
      <Input
        value={line}
        onChange={(e) => setLine(e.target.value)}
        placeholder="Write one line…"
        data-testid={DASHBOARD.intentionInput}
        className="mt-4 border-border bg-card-muted"
      />
      <p className="mt-4 text-[13px] text-muted-foreground">How was today?</p>
      <div className="mt-2 flex gap-2">
        <MoodPill active={mood === "victory"} onClick={() => setMood(mood === "victory" ? "" : "victory")} tone="success">
          Victory
        </MoodPill>
        <MoodPill active={mood === "setback"} onClick={() => setMood(mood === "setback" ? "" : "setback")} tone="warning">
          Setback
        </MoodPill>
      </div>
      <div className="mt-auto flex items-end justify-between gap-3 border-t border-border/60 pt-4">
        <p className="text-[12px] leading-relaxed text-muted-foreground">
          Writing one line is enough.
          <br />
          There is no streak.
        </p>
        <Button size="sm" onClick={save} data-testid={DASHBOARD.intentionSave}>
          Save
        </Button>
      </div>
    </Card>
  );
};

export const TodaySection = () => (
  <FadeIn>
    <section>
      <SectionHeader title="Today" caption={today} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.5fr_1fr]">
        <Card className="p-5 shadow-soft">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <CounterBlock data={counters[0]} />
            <CounterBlock
              data={counters[1]}
              className="border-t border-border pt-6 sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0"
            />
          </div>
        </Card>
        <IntentionCard />
      </div>
    </section>
  </FadeIn>
);
